import React, { FC, useRef, useState } from 'react';
import DialogBox from '../DialogBox';
import Button from '../Button/Button';
import classes from './PinGate.module.css';

const PIN_LENGTH = 4;

const PinGate: FC<{ pin: string; onUnlock: () => void }> = ({ pin, onUnlock }) => {
  const [ digits, setDigits ] = useState<string[]>(Array(PIN_LENGTH).fill(''));
  const [ error, setError ] = useState('');
  const inputsRef = useRef<Array<HTMLInputElement | null>>([]);

  const handleChange = (index: number, value: string) => {
    const entered = value.replace(/\D/g, '');
    if (!entered) {
      setDigits((prev) => prev.map((digit, i) => (i === index ? '' : digit)));
      return;
    }
    setDigits((prev) => {
      const next = [ ...prev ];
      entered.split('').forEach((digit, offset) => {
        if (index + offset < PIN_LENGTH) next[index + offset] = digit;
      });
      return next;
    });
    setError('');
    inputsRef.current[Math.min(index + entered.length, PIN_LENGTH - 1)]?.focus();
  };

  const handleKeyDown = (index: number, event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputsRef.current[index - 1]?.focus();
    }
    if (event.key === 'Enter') {
      handleSubmit(event);
    }
  };

  const handleSubmit = (event: React.SyntheticEvent) => {
    event.preventDefault();
    const entered = digits.join('');
    if (entered.length < PIN_LENGTH) {
      setError('Enter all 4 digits.');
      return;
    }
    if (entered !== pin) {
      setError('Incorrect PIN. Please try again.');
      setDigits(Array(PIN_LENGTH).fill(''));
      inputsRef.current[0]?.focus();
      return;
    }
    onUnlock();
  };

  return (
    <DialogBox
      isOpen={true}
      closeModal={() => {
      }}
      isIcon={false}
      title={''}
      panelClassName={classes.panel}
    >
      <form className={classes.form} onSubmit={handleSubmit}>
        <p className={classes.label}>Enter PIN</p>
        <div className={classes.inputGroup}>
          <div className={classes.inputs}>
            {digits.map((digit, index) => (
              <input
                key={index}
                ref={(element) => {
                  inputsRef.current[index] = element;
                }}
                className={classes.input}
                type={'text'}
                inputMode={'numeric'}
                autoComplete={'off'}
                maxLength={PIN_LENGTH}
                value={digit}
                autoFocus={index === 0}
                aria-label={`PIN digit ${index + 1}`}
                onChange={(event) => handleChange(index, event.target.value)}
                onKeyDown={(event) => handleKeyDown(index, event)}
              />
            ))}
          </div>
          <p className={classes.error} aria-live={'polite'}>{error}</p>
        </div>
        <Button type={'submit'} buttonType={'btnPrimary'} className={classes.submit}>
          Submit
        </Button>
      </form>
    </DialogBox>
  );
};

export default PinGate;
